import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  Network,
  GitBranch,
  Layers,
  Package,
  Image,
  FileText,
  Search,
  BarChart3,
  Building2,
  Users,
  Briefcase,
  Handshake,
  ClipboardList,
  Settings,
  ScrollText,
  ChevronLeft,
  ChevronRight,
  Plug,
  Globe,
  ClipboardCheck,
  Calendar,
  type LucideIcon,
} from 'lucide-react';
import { usePermissions, PERMISSION_CODES, type PermissionCode } from '@/lib/permissions';

interface SJAdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Any of these permissions unlocks the item. Empty = admins only. */
  anyOf?: PermissionCode[];
  adminOnly?: boolean;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        label: 'Dashboard',
        href: '/admin',
        icon: LayoutDashboard,
        anyOf: [PERMISSION_CODES.ADMIN_ACCESS],
        adminOnly: true,
      },
    ],
  },
  {
    title: 'Content',
    items: [
      {
        label: 'Knowledge',
        href: '/admin/knowledge',
        icon: BookOpen,
        anyOf: [PERMISSION_CODES.KNOWLEDGE_CREATE, PERMISSION_CODES.KNOWLEDGE_UPDATE, PERMISSION_CODES.ADMIN_ACCESS],
      },
      {
        label: 'Reviews',
        href: '/admin/reviews',
        icon: ClipboardCheck,
        anyOf: [PERMISSION_CODES.KNOWLEDGE_PUBLISH, PERMISSION_CODES.ADMIN_ACCESS],
      },
      {
        label: 'Business Ontology',
        href: '/admin/ontology',
        icon: Network,
        adminOnly: true,
      },
      {
        label: 'Workflows',
        href: '/admin/workflows',
        icon: GitBranch,
        anyOf: [PERMISSION_CODES.WORKFLOW_CREATE, PERMISSION_CODES.ADMIN_ACCESS],
      },
      {
        label: 'Capabilities',
        href: '/admin/capabilities',
        icon: Layers,
        adminOnly: true,
      },
      {
        label: 'Products',
        href: '/admin/products',
        icon: Package,
        adminOnly: true,
      },
      {
        label: 'Team',
        href: '/admin/team',
        icon: Users,
        adminOnly: true,
      },
      {
        label: 'Associate Program',
        href: '/admin/associates',
        icon: Briefcase,
        adminOnly: true,
      },
      {
        label: 'Partners',
        href: '/admin/partners',
        icon: Handshake,
        adminOnly: true,
      },
      {
        label: 'Partner Applications',
        href: '/admin/partners/applications',
        icon: ClipboardList,
        adminOnly: true,
      },
      {
        label: 'Media Library',
        href: '/admin/media',
        icon: Image,
        anyOf: [PERMISSION_CODES.KNOWLEDGE_CREATE, PERMISSION_CODES.ADMIN_ACCESS],
      },
    ],
  },
  {
    title: 'Intelligence',
    items: [
      { label: 'Prompt Library', href: '/admin/prompts', icon: FileText, adminOnly: true },
      { label: 'Search', href: '/admin/search', icon: Search, adminOnly: true },
      { label: 'Analytics', href: '/admin/analytics', icon: BarChart3, adminOnly: true },
    ],
  },
  {
    title: 'Platform',
    items: [
      { label: 'Demo Requests', href: '/admin/demos', icon: Calendar, adminOnly: true },
      { label: 'SEO', href: '/admin/seo', icon: Globe, adminOnly: true },
      { label: 'API & Integrations', href: '/admin/api', icon: Plug, adminOnly: true },
      { label: 'Organizations', href: '/admin/organizations', icon: Building2, adminOnly: true },
      { label: 'Users & Roles', href: '/admin/users', icon: Users, adminOnly: true },
      { label: 'Settings', href: '/admin/settings', icon: Settings, adminOnly: true },
      { label: 'Audit Logs', href: '/admin/audit', icon: ScrollText, adminOnly: true },
    ],
  },
];

export function SJAdminSidebar({ collapsed, onToggle }: SJAdminSidebarProps) {
  const location = useLocation();
  const { isAdmin, checkAnyPermission } = usePermissions();

  function canSeeItem(item: NavItem): boolean {
    if (isAdmin) return true;
    if (item.adminOnly) return false;
    if (item.anyOf && item.anyOf.length > 0) {
      return checkAnyPermission(item.anyOf);
    }
    return false;
  }

  function isActive(href: string) {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  }

  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter(canSeeItem),
    }))
    .filter((section) => section.items.length > 0);

  const workspaceHome = isAdmin ? '/admin' : '/admin/knowledge';

  return (
    <aside
      className={`relative flex flex-col border-r border-[var(--sj-border)] bg-[var(--sj-surface)] transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div className="flex h-14 items-center justify-between border-b border-[var(--sj-border)] px-3">
        {!collapsed && (
          <Link to={workspaceHome} className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-[#de4c49]">
              <span className="text-xs font-bold text-white">S</span>
            </div>
            <span className="text-sm font-semibold text-[var(--sj-text-primary)]">
              {isAdmin ? 'Admin' : 'Workspace'}
            </span>
          </Link>
        )}
        <button
          onClick={onToggle}
          className="flex h-7 w-7 items-center justify-center rounded-[6px] text-[var(--sj-text-muted)] hover:bg-[var(--sj-bg)] hover:text-[var(--sj-text-primary)] transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {visibleSections.map((section, si) => (
          <div key={si} className="mb-2">
            {section.title && !collapsed && (
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--sj-text-muted)]">
                {section.title}
              </p>
            )}
            {section.title && collapsed && <div className="mx-3 my-2 h-px bg-[var(--sj-border)]" />}
            {section.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`mx-2 mb-0.5 flex items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-xs font-medium transition-colors ${
                    active
                      ? 'bg-[#de4c49]/10 text-[#de4c49]'
                      : 'text-[var(--sj-text-secondary)] hover:bg-[var(--sj-bg)] hover:text-[var(--sj-text-primary)]'
                  }`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-[#de4c49]' : ''}`} />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-[var(--sj-border)] p-3">
        <Link
          to="/"
          className="flex items-center gap-2 rounded-[8px] px-2.5 py-2 text-xs font-medium text-[var(--sj-text-muted)] hover:bg-[var(--sj-bg)] hover:text-[var(--sj-text-primary)] transition-colors"
        >
          {!collapsed && <span>Back to Site</span>}
        </Link>
      </div>
    </aside>
  );
}
